import React, { useState, useEffect, useRef } from 'react';
import { useShop } from '../context/ShopContext';
import { checkDateAvailability } from '../services/availabilityEngine';
import {
  X,
  Trash2,
  ShoppingBag,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  ChevronRight,
  ShieldCheck,
  Sparkles,
  ArrowLeft,
  AlertCircle,
  User,
  Mail,
  Phone,
  MessageSquare,
  Edit3,
  Loader2,
  RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import { CollectionDatePicker } from './CollectionDatePicker';
import { getSizeStock } from '../services/inventoryService';

export const BasketDrawer: React.FC = () => {
  const {
    basket,
    isBasketOpen,
    setIsBasketOpen,
    removeFromBasket,
    updateBasketQuantity,
    createReservation,
    setActivePage,
    schools,
    settings,
    reservations,
    showToast
  } = useShop();

  // 3-step checkout flow: 'cart' -> 'form' -> 'review'
  const [checkoutStep, setCheckoutStep] = useState<'cart' | 'form' | 'review'>('cart');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form Fields - Always initialized to clean empty defaults
  const [parentName, setParentName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredCollectionDay, setPreferredCollectionDay] = useState('');
  const [optionalMessage, setOptionalMessage] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Accessibility refs for focus trapping & restore
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Form Validation Errors
  const [formErrors, setFormErrors] = useState<{
    parentName?: string;
    email?: string;
    phone?: string;
    preferredCollectionDay?: string;
    acceptTerms?: string;
  }>({});

  const totalItemCount = basket.reduce((sum, item) => sum + item.quantity, 0);

  // Focus trap & Escape key handler
  useEffect(() => {
    if (!isBasketOpen) return;

    previousActiveElementRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleOpenClose(false);
        return;
      }

      if (e.key === 'Tab' && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Auto-focus first interactive element inside drawer
    const timer = setTimeout(() => {
      if (drawerRef.current) {
        const firstBtn = drawerRef.current.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])');
        firstBtn?.focus();
      }
    }, 50);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
      if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isBasketOpen]);

  // Reset all form fields to pristine blank state and clear temporary cached state
  const resetReservationForm = () => {
    setParentName('');
    setEmail('');
    setPhone('');
    setPreferredCollectionDay('');
    setOptionalMessage('');
    setAcceptTerms(false);
    setFormErrors({});
    setCheckoutStep('cart');
    try {
      sessionStorage.removeItem('ul_checkout_form');
      localStorage.removeItem('ul_checkout_form');
    } catch (e) {
      // Ignore storage errors
    }
  };

  // If the basket becomes completely empty while in checkout, revert to cart step
  useEffect(() => {
    if (basket.length === 0 && checkoutStep !== 'cart') {
      setCheckoutStep('cart');
    }
  }, [basket.length, checkoutStep]);

  const handleOpenClose = (open: boolean) => {
    if (isSubmitting) return;
    setIsBasketOpen(open);
    // If closed while on review step, return to form or cart
    if (!open && checkoutStep === 'review') {
      setCheckoutStep('cart');
    }
  };

  // Step 2 -> Step 3: Validate form fields before advancing to Review step
  const handleProceedToReview = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof formErrors = {};

    const trimmedName = parentName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName || trimmedName.length < 2) {
      errors.parentName = 'Please enter the Parent or Carer full name (at least 2 characters).';
    }

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = 'Please enter a valid email address (e.g. name@example.co.uk).';
    }

    if (!trimmedPhone || trimmedPhone.replace(/\D/g, '').length < 7) {
      errors.phone = 'Please enter a valid contact phone number (at least 7 digits).';
    }

    if (!preferredCollectionDay) {
      errors.preferredCollectionDay = 'Please select a collection date from the calendar.';
    } else {
      const baseDate = preferredCollectionDay.split(' • ')[0].split(' (')[0].trim();
      const dateObj = new Date(baseDate);
      if (!isNaN(dateObj.getTime())) {
        const status = checkDateAvailability(dateObj, { siteSettings: settings, existingReservations: reservations });
        if (!status.isAvailable) {
          errors.preferredCollectionDay = 'This collection slot is currently full or unavailable. Please choose another date.';
        }
      }
    }

    if (!acceptTerms) {
      errors.acceptTerms = 'You must agree to collect the items within 7 days.';
    }

    if (basket.length === 0) {
      showToast('Your reservation basket is empty.', 'error');
      setCheckoutStep('cart');
      return;
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      showToast(firstError || 'Please complete all required fields.', 'error');
      return;
    }

    // Advance to Safeguard Confirmation / Review step
    setCheckoutStep('review');
  };

  // Step 3: Final confirmation action
  const handleFinalConfirmReservation = async () => {
    if (isSubmitting || basket.length === 0) return;

    // Final safety validation
    const trimmedName = parentName.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    const trimmedDay = preferredCollectionDay.trim();

    if (!trimmedName || !trimmedEmail || !trimmedPhone || !trimmedDay || !acceptTerms) {
      showToast('Missing required reservation details. Please check your form.', 'error');
      setCheckoutStep('form');
      return;
    }

    // Re-verify availability
    const baseDate = trimmedDay.split(' • ')[0].split(' (')[0].trim();
    const dateObj = new Date(baseDate);
    if (!isNaN(dateObj.getTime())) {
      const status = checkDateAvailability(dateObj, { siteSettings: settings, existingReservations: reservations });
      if (!status.isAvailable) {
        showToast('Sorry, this collection slot has just become unavailable. Please choose another date.', 'error');
        setCheckoutStep('form');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await createReservation({
        parentName: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone,
        preferredCollectionDay: trimmedDay,
        optionalMessage: optionalMessage.trim()
      });

      // Complete reset: clear all fields, cached data, and state
      resetReservationForm();

      // Close drawer & redirect to Confirmation page
      setIsBasketOpen(false);
      setActivePage('confirmation');

      // Trigger Celebration Confetti!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (err: any) {
      console.error('Reservation submission error:', err);
      showToast(err.message || 'Failed to complete reservation. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isBasketOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-hidden">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => handleOpenClose(false)}
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        />

        <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
          <motion.div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Shopping Basket"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="w-screen max-w-md bg-white/95 backdrop-blur-2xl border-l border-white/80 shadow-2xl flex flex-col justify-between focus:outline-none"
            tabIndex={-1}
          >
            {/* Header */}
            <div className="p-5 bg-[#002366]/95 backdrop-blur-xl text-white flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-blue-600/30 text-sky-300 border border-blue-400/30">
                  <ShoppingBag className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold text-base text-white">
                    {checkoutStep === 'cart' && 'Reservation Basket'}
                    {checkoutStep === 'form' && 'Collection Details'}
                    {checkoutStep === 'review' && 'Review & Confirm'}
                  </h2>
                  <p className="text-[11px] text-sky-200 font-medium">
                    100% Free Community Service • Immingham
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleOpenClose(false)}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center p-2 text-slate-300 hover:text-white rounded-xl hover:bg-slate-800 transition"
                aria-label="Close basket"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stepper Indicator */}
            {basket.length > 0 && (
              <div className="px-5 py-2.5 bg-slate-100/80 border-b border-slate-200/80 flex items-center justify-between text-[11px] font-semibold text-slate-600">
                <div
                  className={`flex items-center gap-1.5 ${
                    checkoutStep === 'cart'
                      ? 'text-blue-700 font-bold'
                      : 'text-slate-500'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                      checkoutStep === 'cart'
                        ? 'bg-blue-600 text-white'
                        : 'bg-emerald-600 text-white'
                    }`}
                  >
                    1
                  </span>
                  <span>Basket</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                <div
                  className={`flex items-center gap-1.5 ${
                    checkoutStep === 'form'
                      ? 'text-blue-700 font-bold'
                      : checkoutStep === 'review'
                      ? 'text-emerald-700 font-semibold'
                      : 'text-slate-500'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                      checkoutStep === 'form'
                        ? 'bg-blue-600 text-white'
                        : checkoutStep === 'review'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-300 text-slate-600'
                    }`}
                  >
                    2
                  </span>
                  <span>Details</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                <div
                  className={`flex items-center gap-1.5 ${
                    checkoutStep === 'review'
                      ? 'text-blue-700 font-bold'
                      : 'text-slate-500'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                      checkoutStep === 'review'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-300 text-slate-600'
                    }`}
                  >
                    3
                  </span>
                  <span>Review</span>
                </div>
              </div>
            )}

            {/* Body Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* STEP 1: CART LIST */}
              {checkoutStep === 'cart' && (
                <>
                  {basket.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-4 my-auto">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                        <ShoppingBag className="w-8 h-8" />
                      </div>
                      <h3 className="font-bold text-slate-800 text-base">Your reservation basket is empty</h3>
                      <p className="text-xs text-slate-500 leading-relaxed max-w-xs">
                        Browse our stock of donated school uniforms for Immingham schools and reserve items free of charge.
                      </p>
                      <button
                        onClick={() => {
                          handleOpenClose(false);
                          setActivePage('shop');
                        }}
                        className="px-5 py-2.5 rounded-xl bg-[#002366] text-white font-bold text-xs shadow-md hover:bg-blue-900 transition min-h-[44px]"
                      >
                        Browse Uniforms
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200/80 text-xs text-emerald-900 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>
                          All uniforms are <strong>100% Free</strong>. No payments or card details required.
                        </span>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {basket.map((item) => {
                          const maxStock = getSizeStock(item.product, item.selectedSize);
                          const schoolObj = schools.find((s) => s.id === item.product.schoolId);

                          return (
                            <div key={`${item.product.id}-${item.selectedSize}`} className="py-3 flex items-center gap-3">
                              <img
                                src={item.product.images[0]}
                                alt={item.product.name}
                                loading="lazy"
                                decoding="async"
                                className="w-16 h-16 rounded-xl object-cover border border-slate-200 shrink-0 bg-slate-50"
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-xs text-slate-900 truncate">
                                  {item.product.name}
                                </h4>
                                <p className="text-[11px] text-slate-500 font-medium truncate">
                                  {schoolObj?.name || 'Local School'}
                                </p>
                                <p className="text-[11px] text-slate-600 font-medium mt-0.5">
                                  Size:{' '}
                                  <strong className="text-blue-900 font-bold bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                                    {item.selectedSize || item.product.size}
                                  </strong>
                                </p>
                                <div className="flex items-center gap-2 mt-1.5">
                                  <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden text-xs bg-white">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateBasketQuantity(item.product.id, item.selectedSize, item.quantity - 1)
                                      }
                                      className="min-w-[36px] min-h-[36px] flex items-center justify-center hover:bg-slate-100 text-slate-600 font-bold"
                                      title="Decrease quantity"
                                      aria-label={`Decrease quantity of ${item.product.name} size ${item.selectedSize}`}
                                    >
                                      -
                                    </button>
                                    <span className="px-2 font-bold text-slate-900 min-w-[20px] text-center">
                                      {item.quantity}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateBasketQuantity(item.product.id, item.selectedSize, item.quantity + 1)
                                      }
                                      disabled={item.quantity >= maxStock}
                                      title={
                                        item.quantity >= maxStock
                                          ? `Maximum stock (${maxStock}) reached for size ${item.selectedSize}`
                                          : 'Increase quantity'
                                      }
                                      aria-label={`Increase quantity of ${item.product.name} size ${item.selectedSize}`}
                                      className={`min-w-[36px] min-h-[36px] flex items-center justify-center font-bold transition ${
                                        item.quantity >= maxStock
                                          ? 'text-slate-300 bg-slate-50 cursor-not-allowed'
                                          : 'hover:bg-slate-100 text-slate-600'
                                      }`}
                                    >
                                      +
                                    </button>
                                  </div>
                                  <span className="text-[11px] text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                    FREE
                                  </span>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => removeFromBasket(item.product.id, item.selectedSize)}
                                className="min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-500 hover:text-rose-600 transition rounded-xl hover:bg-rose-50 p-2.5"
                                title="Remove item"
                                aria-label={`Remove ${item.product.name} Size ${item.selectedSize || item.product.size} from basket`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* STEP 2: CHECKOUT FORM */}
              {checkoutStep === 'form' && (
                <form id="reservation-form" onSubmit={handleProceedToReview} className="space-y-4 text-xs">
                  <div className="flex items-center justify-between text-slate-500 font-semibold mb-1">
                    <button
                      type="button"
                      onClick={() => setCheckoutStep('cart')}
                      className="p-2 min-h-[44px] text-slate-600 hover:text-slate-900 flex items-center gap-1 font-bold transition rounded-xl"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Back to Basket
                    </button>
                    <button
                      type="button"
                      onClick={resetReservationForm}
                      className="text-[11px] text-slate-500 hover:text-rose-600 flex items-center gap-1 transition p-2"
                      title="Clear all fields"
                    >
                      <RotateCcw className="w-3 h-3" /> Reset Form
                    </button>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      Parent / Carer Full Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        placeholder="e.g. Sarah Jenkins"
                        value={parentName}
                        onChange={(e) => {
                          setParentName(e.target.value);
                          if (formErrors.parentName) setFormErrors((prev) => ({ ...prev, parentName: undefined }));
                        }}
                        className={`w-full px-4 py-2.5 border rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          formErrors.parentName ? 'border-rose-400 bg-rose-50/30' : 'border-slate-200'
                        }`}
                      />
                    </div>
                    {formErrors.parentName && (
                      <p className="text-rose-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {formErrors.parentName}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="sarah@example.co.uk"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (formErrors.email) setFormErrors((prev) => ({ ...prev, email: undefined }));
                        }}
                        className={`w-full px-4 py-2.5 border rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          formErrors.email ? 'border-rose-400 bg-rose-50/30' : 'border-slate-200'
                        }`}
                      />
                      {formErrors.email && (
                        <p className="text-rose-600 text-[10px] font-medium mt-1">
                          {formErrors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        Telephone Number *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="07700 900123"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (formErrors.phone) setFormErrors((prev) => ({ ...prev, phone: undefined }));
                        }}
                        className={`w-full px-4 py-2.5 border rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                          formErrors.phone ? 'border-rose-400 bg-rose-50/30' : 'border-slate-200'
                        }`}
                      />
                      {formErrors.phone && (
                        <p className="text-rose-600 text-[10px] font-medium mt-1">
                          {formErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <CollectionDatePicker
                      selectedDate={preferredCollectionDay}
                      onSelectDate={(formatted) => {
                        setPreferredCollectionDay(formatted);
                        if (formErrors.preferredCollectionDay) {
                          setFormErrors((prev) => ({ ...prev, preferredCollectionDay: undefined }));
                        }
                      }}
                      siteSettings={settings}
                      existingReservations={reservations}
                    />
                    {formErrors.preferredCollectionDay && (
                      <p className="text-rose-600 text-[11px] font-medium mt-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {formErrors.preferredCollectionDay}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      Optional Notes for Volunteers
                    </label>
                    <textarea
                      rows={2}
                      placeholder="e.g. Preferred time of arrival, sizing query, or collection notes..."
                      value={optionalMessage}
                      onChange={(e) => setOptionalMessage(e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div className="pt-2">
                    <label className="flex items-start gap-2.5 cursor-pointer select-none p-2.5 rounded-xl border border-slate-200 bg-slate-50/70 hover:bg-slate-100/70 transition">
                      <input
                        type="checkbox"
                        required
                        checked={acceptTerms}
                        onChange={(e) => {
                          setAcceptTerms(e.target.checked);
                          if (formErrors.acceptTerms) setFormErrors((prev) => ({ ...prev, acceptTerms: undefined }));
                        }}
                        className="mt-0.5 rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <span className="text-[11px] text-slate-700 leading-tight">
                        I agree to collect this reservation within 7 days at Bert Boyden Community Centre, Immingham. I understand all uniforms are provided 100% free of charge.
                      </span>
                    </label>
                    {formErrors.acceptTerms && (
                      <p className="text-rose-600 text-[11px] font-medium mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {formErrors.acceptTerms}
                      </p>
                    )}
                  </div>
                </form>
              )}

              {/* STEP 3: REVIEW & CONFIRM (Safeguard Step) */}
              {checkoutStep === 'review' && (
                <div className="space-y-4 text-xs">
                  <div className="flex items-center justify-between text-slate-500 font-semibold mb-1">
                    <button
                      type="button"
                      onClick={() => setCheckoutStep('form')}
                      className="p-1 text-slate-600 hover:text-slate-900 flex items-center gap-1 font-bold transition"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Edit Details
                    </button>
                    <span className="text-[11px] text-emerald-600 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Details Verified
                    </span>
                  </div>

                  <div className="p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-2xl">
                    <h3 className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-blue-700" />
                      Review Your Reservation Summary
                    </h3>
                    <p className="text-[11px] text-slate-600">
                      Please check the details below carefully before placing your reservation.
                    </p>
                  </div>

                  {/* Reserved Items Summary Card */}
                  <div className="p-3.5 bg-white border border-slate-200 rounded-2xl space-y-2.5 shadow-sm">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <span className="font-bold text-slate-800 flex items-center gap-1.5 text-xs">
                        <ShoppingBag className="w-3.5 h-3.5 text-blue-600" />
                        Reserved Items ({totalItemCount})
                      </span>
                      <button
                        type="button"
                        onClick={() => setCheckoutStep('cart')}
                        className="text-[11px] text-blue-600 hover:underline font-semibold flex items-center gap-0.5"
                      >
                        <Edit3 className="w-3 h-3" /> Change
                      </button>
                    </div>

                    <div className="space-y-2">
                      {basket.map((item) => (
                        <div
                          key={`${item.product.id}-${item.selectedSize}`}
                          className="flex items-center justify-between text-xs py-1"
                        >
                          <div className="flex items-center gap-2 min-w-0 pr-2">
                            <img
                              src={item.product.images[0]}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              className="w-8 h-8 rounded-lg object-cover border border-slate-200 shrink-0"
                            />
                            <div className="min-w-0 truncate">
                              <p className="font-bold text-slate-800 truncate text-[11px]">
                                {item.product.name}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                Size: <span className="font-bold text-slate-700">{item.selectedSize}</span> • Qty: {item.quantity}
                              </p>
                            </div>
                          </div>
                          <span className="text-[11px] font-bold text-emerald-600 shrink-0">
                            FREE
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Collection Date & Location Card */}
                  <div className="p-3.5 bg-white border border-slate-200 rounded-2xl space-y-2 shadow-sm">
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                      <span className="font-bold text-slate-800 flex items-center gap-1.5 text-xs">
                        <Calendar className="w-3.5 h-3.5 text-blue-600" />
                        Collection Day &amp; Venue
                      </span>
                      <button
                        type="button"
                        onClick={() => setCheckoutStep('form')}
                        className="text-[11px] text-blue-600 hover:underline font-semibold flex items-center gap-0.5"
                      >
                        <Edit3 className="w-3 h-3" /> Edit Day
                      </button>
                    </div>

                    <div className="bg-sky-50/60 p-2.5 rounded-xl border border-sky-100 space-y-1">
                      <div className="flex items-center gap-1.5 text-slate-900 font-black text-xs">
                        <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span>{preferredCollectionDay}</span>
                      </div>
                      <div className="flex items-start gap-1.5 text-slate-600 text-[11px]">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-slate-800">
                            {settings.collectionLocation || 'Bert Boyden Community Centre'}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {settings.collectionAddress || '34 Carver Road, Immingham DN40 1DS'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Parent / Carer Information Card */}
                  <div className="p-3.5 bg-white border border-slate-200 rounded-2xl space-y-2 shadow-sm">
                    <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                      <span className="font-bold text-slate-800 flex items-center gap-1.5 text-xs">
                        <User className="w-3.5 h-3.5 text-blue-600" />
                        Parent / Carer Contact Details
                      </span>
                      <button
                        type="button"
                        onClick={() => setCheckoutStep('form')}
                        className="text-[11px] text-blue-600 hover:underline font-semibold flex items-center gap-0.5"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                    </div>

                    <div className="space-y-1.5 text-[11px]">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-20">Parent/Carer:</span>
                        <strong className="text-slate-800">{parentName}</strong>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-20">Email:</span>
                        <strong className="text-slate-800">{email}</strong>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-20">Phone:</span>
                        <strong className="text-slate-800">{phone}</strong>
                      </div>
                      {optionalMessage && (
                        <div className="flex items-start gap-2 pt-1 border-t border-slate-100">
                          <span className="text-slate-500 w-20 shrink-0">Notes:</span>
                          <span className="text-slate-700 italic">{optionalMessage}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-[11px] text-amber-900 leading-relaxed">
                    <strong>Notice:</strong> Please click the button below to confirm your booking. Our volunteer team will pack your order for collection on your chosen date.
                  </div>
                </div>
              )}
            </div>

            {/* Footer Navigation Buttons */}
            {basket.length > 0 && (
              <div className="p-5 border-t border-slate-200 bg-slate-50 space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-medium">Total Cost:</span>
                  <div className="text-right">
                    <span className="font-extrabold text-emerald-600 text-base">£0.00 FREE</span>
                  </div>
                </div>

                {/* Step 1 Button: Proceed to Details */}
                {checkoutStep === 'cart' && (
                  <button
                    onClick={() => setCheckoutStep('form')}
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-[#002366] hover:from-blue-700 hover:to-blue-900 text-white font-bold text-sm shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 transition"
                  >
                    <span>Proceed to Collection Details ({totalItemCount} items)</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}

                {/* Step 2 Button: Review Reservation */}
                {checkoutStep === 'form' && (
                  <button
                    type="submit"
                    form="reservation-form"
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-[#002366] hover:from-blue-700 hover:to-blue-900 text-white font-bold text-sm shadow-lg flex items-center justify-center gap-2 transition"
                  >
                    <span>Review Reservation Details</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}

                {/* Step 3 Button: Final Confirm Reservation */}
                {checkoutStep === 'review' && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={handleFinalConfirmReservation}
                      disabled={isSubmitting}
                      className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-black text-sm shadow-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Creating Your Free Reservation...</span>
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-5 h-5" />
                          <span>Confirm Free Reservation</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCheckoutStep('form')}
                      disabled={isSubmitting}
                      className="w-full py-2 text-center text-xs font-semibold text-slate-500 hover:text-slate-800 transition"
                    >
                      Change Details or Collection Date
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};
